/* eslint-disable @typescript-eslint/no-empty-function */
import React, {
  useState,
  useCallback,
  useEffect,
  Dispatch,
  SetStateAction,
  useRef,
} from "react";

import {
  makeStyles,
  Toolbar,
  Typography,
  CardContent,
  Card,
  Grid,
  Checkbox,
  TableCell,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Hidden,
  Tooltip,
} from "@material-ui/core";
import { People, PeopleOutline } from "@material-ui/icons";
import { green as myGreen, red as myRed } from "@material-ui/core/colors";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, ChartData } from "chart.js";
import { CallSummaryEvent } from "./Hud";
import { QueueCallState, Summary } from "./QueueLeaderboard";
import { CXAgent, CXExtension, CXQueue } from "./CxDashboard";

ChartJS.register(ArcElement);

const formatDuration = (time: number) => {
  const durationSeconds = time;
  const durationMinutes = Math.floor(durationSeconds / 60);
  const durationHours =
    durationMinutes >= 60 ? Math.floor(durationMinutes / 60) : "";
  const seconds = durationSeconds % 60;
  const minutes = durationMinutes % 60;
  return durationHours !== ""
    ? `${durationHours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    : `${durationMinutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
};

const findAnsweredAverage = (extension: CXExtension) => {
  if (extension.answeredCount === undefined || extension.answeredCount === 0) {
    return "--:--";
  }
  const avg = (extension.answeredWaiting || 0) / extension.answeredCount;
  return formatDuration(Math.round(avg));
};

const findAbandonedAverage = (extension: CXExtension) => {
  if (
    extension.abandonedCount === undefined ||
    extension.abandonedCount === 0
  ) {
    return "--:--";
  }
  const avg = (extension.abandonedWaiting || 0) / extension.abandonedCount;
  return formatDuration(Math.round(avg));
};
const findVoicemailAverage = (extension: CXExtension) => {
  if (
    extension.voicemailCount === undefined ||
    extension.voicemailCount === 0
  ) {
    return "--:--";
  }
  const avg = (extension.voicemailWaiting || 0) / extension.voicemailCount;
  return formatDuration(Math.round(avg));
};

function Time({
  time,
  offset,
}: {
  time: string | number | undefined;
  offset: number;
}) {
  const [duration, setDuration] = useState<string | number | undefined>(
    "00:00"
  );

  useEffect(() => {
    const updateTimer = () => {
      if (time) {
        const localizedTime =
          new Date(time).getTime() + new Date(time).getTimezoneOffset() * 60000;
        const diff =
          new Date().getTime() + offset - new Date(localizedTime).getTime();
        setDuration(`${formatDuration(Math.round(diff / 1000))}`);
      }
    };
    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, [time]);

  return <span style={{ fontWeight: "bold" }}>&nbsp; {duration}</span>;
}

const findSize = (map: Map<string, { weight: number }>) =>
  Array.from(map.values()).reduce((sum, { weight = 1 }) => sum + weight, 0);

// const findAverage = (
//   map: Map<string, { duration: number; weight: number }>
// ) => {
//   if (map.size === 0) {
//     return "--:--";
//   }
//   const avg =
//     Array.from(map.values()).reduce(
//       (
//         sum: number,
//         { duration, weight = 1 }: { duration: number; weight: number }
//       ) => sum + duration * weight,
//       0
//     ) / findSize(map);
//   return formatDuration(Math.round(avg / 1000));
// };

const green = {
  ...myGreen,
  1000: "rgba(144, 238, 144, 0.2)",
};

const red = {
  ...myRed,
  1000: "rgba(255, 204, 203, 0.2)",
};

const useStyles = makeStyles({
  caption: {
    flex: "1 1 100%",
    overflowX: "hidden",
    whiteSpace: "nowrap",
  },
  gear: {
    opacity: 0.25,
    "&:hover": {
      opacity: 1,
    },
  },
  root: {
    flexGrow: 1,
    fontFamily: "roboto",
    fontSize: "0.85rem",
  },
  paper: {
    height: 50,
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    justifyContent: "center",
    flexDirection: "column",
    backgroundColor: "#00000000",
    background: "rgb(245, 245, 245)",
  },
  label: {
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    justifyContent: "center",
    flexDirection: "row",
  },
  toolbar: {
    minHeight: 24,
  },
  cardContent: {
    padding: 6,
    "&:last-child": {
      paddingBottom: 6,
    },
  },
  table: {
    borderBottom: "none",
  },
});

const useDarkStyles = makeStyles({
  caption: {
    flex: "1 1 100%",
    overflowX: "hidden",
    whiteSpace: "nowrap",
  },
  gear: {
    opacity: 0.25,
    "&:hover": {
      opacity: 1,
    },
  },
  root: {
    flexGrow: 1,
    fontFamily: "roboto",
    fontSize: "0.85rem",
  },
  paper: {
    height: 50,
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    justifyContent: "center",
    flexDirection: "column",
    backgroundColor: "#00000000",
    background: "linear-gradient(0deg,#0e0e0e,#0e0e0e,#0e0e0e,#1f1f1f);",
  },
  label: {
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    justifyContent: "center",
    flexDirection: "row",
  },
  toolbar: {
    minHeight: 24,
  },
  cardContent: {
    padding: 6,
    "&:last-child": {
      paddingBottom: 6,
    },
  },
  table: {
    borderBottom: "none",
  },
});

const findPercent = (value: number, dataset: number[]) =>
  Math.round((value * 100) / dataset.reduce((a, b) => a + (b || 0), 0));

function CxQueueWidget({
  queue,
  agents,
  offset,
  mode,
  checked,
  setCheckedQueues,
  sidebar,
  extension,
  longestWaitingEvents,
  tab,
}: {
  queue: CXQueue;
  agents:
    | Array<{ extension: CXExtension | undefined; agent: CXAgent }>
    | undefined;
  offset: number;
  extension: CXExtension | undefined;
  mode: string;
  checked: boolean;
  setCheckedQueues: any;
  sidebar: boolean;
  longestWaitingEvents: CXExtension[];
  tab: string;
}) {
  const [agentStatuses, setAgentStatuses] = useState<{
    available: number;
    busy: number;
    unavailable: number;
  }>({
    available: 0,
    busy: 0,
    unavailable: 0,
  });
  const [backgroundWeight, setBackgroundWeight] = useState<100 | 1000>(1000);
  const [color, setColor] = useState<"initial" | "white">("initial");
  const [donutData, setDonutData] =
    useState<ChartData<"doughnut", number[], unknown>>();
  const [agentDonutData, setAgentDonutData] =
    useState<ChartData<"doughnut", number[], unknown>>();
  const [longestWaiting, setLongestWaiting] = useState<
    number | string | undefined
  >();
  const donutRef = useRef<ChartJS<"doughnut", number[], string>>(null);
  const smallerDonutRef = useRef<ChartJS<"doughnut", number[], string>>(null);

  useEffect(() => {
    if (donutRef.current && smallerDonutRef.current) {
      donutRef.current?.render();
      donutRef.current?.render();
    }
  }, [tab]);

  useEffect(() => {
    if (longestWaitingEvents && longestWaitingEvents.length > 0) {
      const oldestDate = longestWaitingEvents.reduce((oldest, current) => {
        const oldestTimestamp = new Date(oldest.timestamp!).getTime();
        const currentTimestamp = new Date(current.timestamp!).getTime();
        return oldestTimestamp < currentTimestamp ? oldest : current;
      }, longestWaitingEvents[0]);
      setLongestWaiting(oldestDate.timestamp);
    } else {
      setLongestWaiting(undefined);
    }
  }, [longestWaitingEvents]);

  useEffect(() => {
    if (mode === "light") {
      setBackgroundWeight(1000);
      return setColor("initial");
    }
    setBackgroundWeight(100);
    return setColor("white");
  }, [mode]);

  useEffect(() => {
    let availableCount = 0;
    let busyCount = 0;
    let unavailableCount = 0;

    agents?.forEach((agent) => {
      if (agent.agent.agentState) {
        if (
          agent.agent.agentState === "IDLE" ||
          agent.agent.agentState === "AVAILABLE"
        ) {
          availableCount += 1;
        }
        if (
          agent.agent.agentState === "TRANSITION" ||
          agent.agent.agentState === "BREAK-AFTER-CALL" ||
          agent.agent.agentState === "ENGAGED"
        ) {
          busyCount += 1;
        }
        if (
          agent.agent.agentState === "AWAY" ||
          agent.agent.agentState === "ON-BREAK" ||
          agent.agent.agentState === "LUNCH" ||
          agent.agent.agentState === "TRAINING" ||
          agent.agent.agentState === "WORKING" ||
          agent.agent.agentState === "OFF-LINE" ||
          agent.agent.agentState === "RNA-STATE"
        ) {
          unavailableCount += 1;
        }
      }
    });
    setAgentStatuses({
      available: availableCount,
      busy: busyCount,
      unavailable: unavailableCount,
    });
  }, [agents]);

  useEffect(() => {
    setDonutData(() => ({
      labels: ["Answered", "Abandoned", "Voicemail", "Other"],
      datasets: [
        {
          data: [
            extension?.answeredCount || 0,
            extension?.abandonedCount || 0,
            extension?.voicemailCount || 0,
            0,
          ],
          backgroundColor: ["green", "brown", "#35baf6", "purple"],
        },
      ],
    }));
  }, [extension]);

  useEffect(() => {
    setAgentDonutData(() => ({
      labels: ["Available", "Busy", "NotAccepting"],
      datasets: [
        {
          data: [
            agentStatuses.available,
            agentStatuses.busy,
            agentStatuses.unavailable,
          ],
          backgroundColor: ["green", "lightpink", "brown"],
        },
      ],
    }));
  }, [agentStatuses]);

  const classes = mode === "light" ? useStyles() : useDarkStyles();

  const renderBg = useCallback(
    (ex) => {
      if (mode === "light") {
        if (ex && ex.waitingCount) {
          return ex.waitingCount > 0 ? "#FFCDD2" : "#C8E6C9";
        }
        return "#C8E6C9";
      }
      if (ex && ex.waitingCount) {
        return ex.waitingCount > 0 ? "#3F3535" : "#2A3D2A";
      }
      return "#2A3D2A";
    },
    [extension, mode]
  );

  return (
    <Grid
      item
      xs={12}
      sm={sidebar ? 12 : 6}
      md={sidebar ? 6 : 4}
      lg={sidebar ? 4 : 3}
    >
      <div
        className="widget-container"
        style={{
          background:
            mode === "light"
              ? "rgb(245, 245, 245)"
              : "linear-gradient(0deg, #0e0e0e, #0e0e0e, #0e0e0e, #1f1f1f)",
          borderRadius: 4,
        }}
      >
        <Card
          classes={{ root: classes.root }}
          style={{
            background:
              mode === "light"
                ? "rgb(245, 245, 245)"
                : "linear-gradient(0deg,#0e0e0e,#0e0e0e,#0e0e0e,#1f1f1f);",
            color,
          }}
        >
          <CardContent>
            <Typography
              component="div"
              style={{
                flex: "1 1 100%",
                overflowX: "hidden",
                whiteSpace: "nowrap",
                textAlign: "left",
                color: mode === "dark" ? "white" : "black",
              }}
            >
              <Tooltip title={checked ? "Hide Agents" : "Show Agents"}>
                <Checkbox
                  color="default"
                  checked={checked}
                  checkedIcon={<People />}
                  icon={<PeopleOutline />}
                  style={
                    mode === "dark"
                      ? { color: "white", float: "right" }
                      : { float: "right" }
                  }
                  onChange={() => {
                    setCheckedQueues(
                      (oldChecked: Set<number | string | undefined>) => {
                        const newChecked = new Set(oldChecked);
                        if (checked) {
                          newChecked.delete(queue.id);
                        } else {
                          newChecked.add(queue.id);
                        }
                        return newChecked;
                      }
                    );
                  }}
                />
              </Tooltip>
              <div style={{ fontWeight: "bold", fontSize: 16 }}>
                {queue.gateName}
              </div>
            </Typography>
          </CardContent>
          <div>
            <Toolbar
              className="toolbar-transition"
              style={{
                backgroundColor: renderBg(extension),
                color,
                padding: "6px !important",
                minHeight: "30px !important",
              }}
            >
              <Grid
                container
                justifyContent="space-evenly"
                alignItems="center"
                style={{
                  marginLeft: 80,
                }}
              >
                <Hidden smUp>
                  <Grid item xs={1} />
                </Hidden>
                <Grid item xs={5} sm={6} style={{ textAlign: "center" }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div
                      style={{
                        height: 10,
                        width: 10,
                        backgroundColor: "green",
                      }}
                    />
                    <Typography
                      component="div"
                      style={{
                        marginLeft: 5,
                        fontSize: 12,
                        textOverflow: "ellipsis",
                        display: "flex",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      Available: {agentStatuses.available}
                    </Typography>
                  </div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div
                      style={{
                        height: 10,
                        width: 10,
                        backgroundColor: "lightpink",
                      }}
                    />
                    <Typography
                      component="div"
                      style={{
                        marginLeft: 5,
                        fontSize: 12,
                        textOverflow: "ellipsis",
                        display: "flex",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      Busy: {agentStatuses.busy}
                    </Typography>
                  </div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div
                      style={{
                        height: 10,
                        width: 10,
                        backgroundColor: "brown",
                      }}
                    />
                    <Typography
                      component="div"
                      style={{
                        marginLeft: 5,
                        fontSize: 12,
                        textOverflow: "ellipsis",
                        display: "flex",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      Unavailable: {agentStatuses.unavailable}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={6} style={{ paddingLeft: 7 }}>
                  <Typography
                    style={{
                      // fontWeight: "bold",
                      fontSize: 12,
                      color: mode === "dark" ? "white" : undefined,
                      textOverflow: "ellipsis",
                      display: "flex",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                    }}
                  >
                    In Progress: {extension?.activeCount || 0}
                  </Typography>
                  <Typography
                    style={{
                      // fontWeight: "bold",
                      fontSize: 12,
                      color: mode === "dark" ? "white" : undefined,
                      textOverflow: "ellipsis",
                      display: "flex",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                    }}
                  >
                    Waiting: {extension?.waitingCount || 0}
                  </Typography>
                  <Typography
                    style={{
                      // fontWeight: "bold",
                      fontSize: 12,
                      color,
                      display: "flex",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Longest:{" "}
                    {longestWaiting ? (
                      <Time time={longestWaiting} offset={offset} />
                    ) : (
                      <> -&nbsp;-&nbsp;:&nbsp;-&nbsp;-</>
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Toolbar>
          </div>
          <div style={{ marginTop: -80, padding: 6, position: "relative" }}>
            <div
              style={{
                height: 90,
                width: 90,
                backgroundColor: mode === "dark" ? "#0a0a0a" : "#f5f5f5",
                borderRadius: "50%",
              }}
            />
          </div>
          <div style={{ marginTop: -112, marginLeft: -10 }}>
            <CardContent style={{ padding: "6px !important" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                {agentDonutData && tab === "cx-queues" && (
                  <Tooltip
                    placement="right-start"
                    title={
                      agents?.map((agent) => (
                        <div
                          style={{ fontSize: "1.3em", paddingBottom: 5 }}
                          key={`${agent.agent.agentId}`}
                        >
                          {agent.agent.firstName} {agent.agent.lastName}
                        </div>
                      )) || ""
                    }
                  >
                    <div
                      className="cx-agent-data-donut"
                      style={{
                        height: 90,
                        width: 90,
                        position: "relative",
                        fontSize: 16,
                        display: "inline-block",
                      }}
                    >
                      <Doughnut
                        data={agentDonutData}
                        ref={donutRef}
                        options={{
                          plugins: {
                            tooltip: false as any,
                            legend: {
                              display: false,
                            },
                          },
                          transitions: {
                            show: {
                              animations: {
                                x: {
                                  from: 0,
                                },
                                y: {
                                  from: 0,
                                },
                              },
                            },
                            hide: {
                              animations: {
                                x: {
                                  to: 0,
                                },
                                y: {
                                  to: 0,
                                },
                              },
                            },
                          },
                          responsive: true,
                          maintainAspectRatio: true,
                          aspectRatio: 1,
                          cutout: "80%",
                          elements: {
                            arc: {
                              borderWidth: 0,
                            },
                          },
                        }}
                      />

                      <div
                        style={{
                          marginTop: -70,
                          textAlign: "center",
                          color,
                        }}
                      >
                        <h5
                          style={{
                            marginBottom: 0,
                            marginTop: 0,
                            fontSize: 20,
                          }}
                        >
                          {agentStatuses.available +
                            agentStatuses.busy +
                            agentStatuses.unavailable}
                        </h5>
                        <span style={{ fontWeight: "bold", fontSize: 13 }}>
                          Agents
                        </span>
                      </div>
                    </div>
                  </Tooltip>
                )}
              </div>
            </CardContent>
          </div>
          <div style={{ marginTop: -20 }}>
            <CardContent style={{ padding: "6px !important" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  color: mode === "dark" ? "white" : undefined,
                }}
              >
                {donutData && tab === "cx-queues" && (
                  <div
                    className="cx-queue-call-data-donut"
                    style={{
                      height: 75,
                      width: 75,
                      position: "relative",
                      fontSize: 16,
                      paddingRight: 15,
                      paddingTop: 25,
                      display: "inline-block",
                    }}
                  >
                    <Doughnut
                      data={donutData}
                      ref={smallerDonutRef}
                      options={{
                        plugins: {
                          tooltip: false as any,
                          legend: {
                            display: false,
                          },
                        },
                        responsive: true,
                        maintainAspectRatio: true,
                        aspectRatio: 1,
                        transitions: {
                          show: {
                            animations: {
                              x: {
                                from: 40,
                              },
                              y: {
                                from: 40,
                              },
                            },
                          },
                          hide: {
                            animations: {
                              x: {
                                to: 40,
                              },
                              y: {
                                to: 40,
                              },
                            },
                          },
                        },
                        cutout: "80%",
                        elements: {
                          arc: {
                            borderWidth: 0,
                          },
                        },
                      }}
                    />
                    <div style={{ marginTop: -59, textAlign: "center" }}>
                      <h5
                        style={{ marginBottom: 0, marginTop: 0, fontSize: 20 }}
                      >
                        {donutData.datasets[0].data.reduce((a, b) => a + b, 0)}
                      </h5>
                      <span style={{ fontWeight: "bold", fontSize: 14 }}>
                        Calls
                      </span>
                    </div>
                  </div>
                )}
                <TableContainer>
                  <Table
                    style={{
                      tableLayout: "fixed",
                      borderCollapse: "separate",
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell
                          style={{
                            fontWeight: 900,
                            fontSize: 12,
                            padding: 0,
                            width: 20,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        />
                        <TableCell
                          style={{
                            fontWeight: 800,
                            fontSize: 12,
                            padding: 0,
                            overflowX: "hidden",
                            whiteSpace: "nowrap",
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          Result
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{
                            fontWeight: 800,
                            fontSize: 12,
                            padding: 0,
                            overflowX: "hidden",
                            whiteSpace: "nowrap",
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          Total
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{
                            fontWeight: 800,
                            fontSize: 12,
                            padding: 0,
                            overflowX: "hidden",
                            whiteSpace: "nowrap",
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          Pct
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{
                            fontWeight: 800,
                            fontSize: 12,
                            padding: 0,
                            overflowX: "hidden",
                            whiteSpace: "nowrap",
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          Avg Wait
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        style={{
                          height: 20,
                        }}
                      >
                        <TableCell
                          style={{
                            fontSize: 12,
                            padding: 0,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          <div
                            style={{
                              height: 10,
                              width: 10,
                              backgroundColor: "green",
                            }}
                          />
                        </TableCell>
                        <TableCell
                          style={{
                            fontSize: 12,
                            padding: 0,
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          Answered
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{
                            fontSize: 12,
                            padding: 0,
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          {extension?.completedCount || 0}
                        </TableCell>
                        <TableCell
                          style={{
                            fontSize: 12,
                            padding: 0,
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                          align="right"
                        >
                          {donutData &&
                          findPercent(
                            donutData?.datasets?.[0]?.data[0] || 0,
                            donutData?.datasets?.[0]?.data
                          ) > 0
                            ? `${findPercent(
                                donutData?.datasets?.[0]?.data[0] || 0,
                                donutData?.datasets?.[0]?.data
                              )}%`
                            : "--"}
                        </TableCell>
                        <TableCell
                          style={{
                            fontSize: 12,
                            padding: 0,
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                          align="right"
                        >
                          {extension ? findAnsweredAverage(extension) : "--:--"}
                        </TableCell>
                      </TableRow>
                      <TableRow style={{ height: 25 }}>
                        <TableCell
                          style={{
                            fontSize: 12,
                            padding: 0,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          <div
                            style={{
                              height: 10,
                              width: 10,
                              backgroundColor: "brown",
                            }}
                          />
                        </TableCell>
                        <TableCell
                          style={{
                            fontSize: 12,
                            padding: 0,
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          Abandoned
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{
                            fontSize: 12,
                            padding: 0,
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          {extension?.abandonedCount || 0}
                        </TableCell>
                        <TableCell
                          style={{
                            fontSize: 12,
                            padding: 0,
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                          align="right"
                        >
                          {donutData &&
                          findPercent(
                            donutData?.datasets?.[0]?.data[1] || 0,
                            donutData?.datasets?.[0]?.data
                          ) > 0
                            ? `${findPercent(
                                donutData?.datasets?.[0]?.data[1] || 0,
                                donutData?.datasets?.[0]?.data
                              )}%`
                            : "--"}
                        </TableCell>
                        <TableCell
                          style={{
                            fontSize: 12,
                            padding: 0,
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                          align="right"
                        >
                          {extension
                            ? findAbandonedAverage(extension)
                            : "--:--"}
                        </TableCell>
                      </TableRow>
                      <TableRow style={{ height: 25 }}>
                        <TableCell
                          style={{
                            fontSize: 12,
                            padding: 0,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          <div
                            style={{
                              height: 10,
                              width: 10,
                              backgroundColor: "#35baf6",
                            }}
                          />
                        </TableCell>
                        <TableCell
                          style={{
                            fontSize: 12,
                            padding: 0,
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          Voicemail
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{
                            fontSize: 12,
                            padding: 0,
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          {extension?.voicemailCount || 0}
                        </TableCell>
                        <TableCell
                          style={{
                            fontSize: 12,
                            padding: 0,
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                          align="right"
                        >
                          {/* {findSize(answered) +
                          findSize(abandoned) +
                          findSize(voicemail) +
                          findSize(missed) >
                        0
                          ? `${Math.round(
                              (findSize(voicemail) /
                                (findSize(answered) +
                                  findSize(abandoned) +
                                  findSize(voicemail) +
                                  findSize(missed))) *
                                100
                            )}%`
                          : "--"} */}{" "}
                          --
                        </TableCell>
                        <TableCell
                          style={{
                            fontSize: 12,
                            padding: 0,
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                          align="right"
                        >
                          {extension
                            ? findVoicemailAverage(extension)
                            : "--:--"}
                        </TableCell>
                      </TableRow>
                      <TableRow style={{ height: 25 }}>
                        <TableCell
                          style={{
                            fontSize: 12,
                            padding: 0,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          <div
                            style={{
                              height: 10,
                              width: 10,
                              backgroundColor: "purple",
                            }}
                          />
                        </TableCell>
                        <TableCell
                          style={{
                            fontSize: 12,
                            padding: 0,
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          Other
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{
                            fontSize: 12,
                            padding: 0,
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          {extension?.transferCount || 0}
                        </TableCell>
                        <TableCell
                          style={{
                            fontSize: 12,
                            padding: 0,
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                          align="right"
                        >
                          {/* {findSize(answered) +
                          findSize(abandoned) +
                          findSize(voicemail) +
                          findSize(missed) >
                        0
                          ? `${Math.round(
                              (findSize(missed) /
                                (findSize(answered) +
                                  findSize(abandoned) +
                                  findSize(voicemail) +
                                  findSize(missed))) *
                                100
                            )}%`
                          : "--"} */}{" "}
                          --
                        </TableCell>
                        <TableCell
                          style={{
                            fontSize: 12,
                            padding: 0,
                            color,
                            borderBottom:
                              mode === "dark"
                                ? "1px solid gray"
                                : "1px solid #E0E0E0",
                          }}
                          align="right"
                        >
                          --:--
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </CardContent>
          </div>
        </Card>
      </div>
    </Grid>
  );
}

export default CxQueueWidget;
